import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useConnectivity } from "@/lib/offline/connectivity";
import { Card } from "@/components/ui/card";
import { Clock, UserRound, ClipboardList, Boxes, PackageCheck, Wifi, WifiOff, Monitor, Zap } from "lucide-react";

const roleBadge = {
  owner: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  manager: "bg-violet-500/15 text-violet-500 border-violet-500/30",
  cashier: "bg-sky-500/15 text-sky-500 border-sky-500/30",
};

export function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export function DashboardHeader() {
  const { user, business } = useAuth();
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good Morning" : hour < 17 ? "Good Afternoon" : "Good Evening";
  return (
    <div className="flex items-center gap-4" data-testid="dashboard-header">
      {business?.logo_url
        ? <img src={business.logo_url} alt="logo" className="w-16 h-16 rounded-2xl object-cover shadow-lg" />
        : <div className="w-16 h-16 rounded-2xl bg-emerald-500 flex items-center justify-center text-white font-head font-extrabold text-3xl shadow-lg shadow-emerald-500/20">{(business?.name || "N")[0]}</div>}
      <div>
        <p className="text-sm font-semibold text-emerald-500">{greeting}, {user?.name?.split(" ")[0]}</p>
        <h1 className="font-head text-3xl lg:text-4xl font-extrabold leading-tight">Welcome to {business?.name}</h1>
        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Zap className="w-3 h-3 text-emerald-500" /> Powered by NEEC POS{business?.opening_hours ? ` · ${business.opening_hours}` : ""}</p>
      </div>
    </div>
  );
}

// Non-monetary operational card. Never pass money values here.
export function OpsCard({ label, value, icon: Icon, accent, sub, testid }) {
  return (
    <Card className="p-5 relative overflow-hidden" data-testid={testid}>
      <div className={`absolute -right-4 -top-4 w-20 h-20 rounded-full ${accent} opacity-10`} />
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className="w-5 h-5 text-muted-foreground" />
      </div>
      <p className="font-head text-xl lg:text-2xl font-bold mt-2 leading-tight">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </Card>
  );
}

export function ClockCard() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return <OpsCard label="Date & Time" icon={Clock} accent="bg-emerald-500" testid="ops-clock"
    value={<span className="mono">{now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}</span>}
    sub={now.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })} />;
}

export function IdentityCard() {
  const { user } = useAuth();
  return <OpsCard label="Signed in as" icon={UserRound} accent="bg-violet-500" testid="ops-identity"
    value={user?.name}
    sub={<span className={`text-[10px] px-1.5 py-0.5 rounded-md border capitalize ${roleBadge[user?.role] || ""}`} data-testid="ops-identity-role">{user?.role}</span>} />;
}

export function MyShiftCard({ shift }) {
  const open = !!shift;
  const where = shift?.opened_on_register || shift?.register;
  return <OpsCard label="My Shift" icon={ClipboardList} accent={open ? "bg-emerald-500" : "bg-slate-500"} testid="ops-my-shift"
    value={<span className={open ? "text-emerald-500" : "text-muted-foreground"} data-testid="ops-shift-state">{open ? "Shift Open" : "Shift Closed"}</span>}
    sub={open ? `Open since ${fmtTime(shift.opened_at)}${where ? ` · ${where}` : ""}` : "Open a shift from the POS Register or Shift Report"} />;
}

export function OpenShiftsCard({ count }) {
  return <OpsCard label="Open Shifts" icon={ClipboardList} accent="bg-emerald-500" testid="ops-open-shifts"
    value={<span className="mono">{count ?? 0}</span>} sub={count === 1 ? "1 register currently trading" : `${count ?? 0} registers currently trading`} />;
}

export function StockAlertCard({ low, out }) {
  return <OpsCard label="Stock Alerts" icon={Boxes} accent="bg-amber-500" testid="ops-stock-alerts"
    value={<span className="mono">{(low || 0) + (out || 0)}</span>} sub={`${low || 0} low · ${out || 0} out of stock`} />;
}

export function PickupsCard({ count }) {
  return <OpsCard label="Pending Pickups" icon={PackageCheck} accent="bg-sky-500" testid="ops-pickups"
    value={<span className="mono">{count ?? 0}</span>} sub="Orders awaiting pickup or delivery" />;
}

export function RegisterCard({ terminal }) {
  const reg = terminal?.register;
  const label = reg?.display_name || reg?.name || (terminal?.status === "legacy" ? "Legacy terminal" : "Not registered");
  return <OpsCard label="Current Register" icon={Monitor} accent="bg-sky-500" testid="ops-register"
    value={label} sub={reg?.location_name ? reg.location_name : terminal?.status === "required" ? "This browser is not a registered POS terminal" : "This device"} />;
}

export function ConnectivityCard() {
  const c = useConnectivity();
  const online = c.status === "ONLINE" || c.status === "SYNCING";
  return <OpsCard label="Connection" icon={online ? Wifi : WifiOff} accent={online ? "bg-emerald-500" : "bg-amber-500"} testid="ops-connectivity"
    value={<span className={online ? "text-emerald-500" : "text-amber-500"}>{c.status === "SYNCING" ? "Syncing" : online ? "Online" : "Offline"}</span>}
    sub={c.pending ? `${c.pending} offline sale${c.pending === 1 ? "" : "s"} waiting to sync` : "Sales and sync are up to date"} />;
}
